import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Modal } from 'components/UI/Modal/Modal'
import { DataTableProvider } from 'components/DataTable'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { UnifiedSearchInput } from 'components/UnifiedSearch/UnifiedSearchInput'
import { UnifiedSearchFilters } from 'components/UnifiedSearch/UnifiedSearchFilters'
import { UnifiedSearchResults } from 'components/UnifiedSearch/UnifiedSearchResults'
import {
  useUnifiedSearchState,
  useUnifiedSearchAPI,
  useUnifiedSearchOptions,
} from 'components/UnifiedSearch/hooks'
import { ContactCompanySearchResult, DoorSearchResult } from 'models/companies'
import { toast } from 'react-toastify'
import apiService from 'services/api'

const PAGE_SIZE = 20

interface CompanySearchModalProps {
  open: boolean
  onClose: () => void
  onSelectCompany: (
    company: ContactCompanySearchResult,
    door?: DoorSearchResult
  ) => void
}

export function CompanySearchModal({
  open,
  onClose,
  onSelectCompany,
}: CompanySearchModalProps) {
  const api = apiService()
  const [selectedCompany, setSelectedCompany] =
    useState<ContactCompanySearchResult | null>(null)
  const [selectedDoor, setSelectedDoor] = useState<DoorSearchResult | null>(
    null
  )
  // Track the originally clicked row for radio button selection
  const [selectedRow, setSelectedRow] = useState<any>(null)
  // Track loading state for company resolution
  const [isResolvingCompany, setIsResolvingCompany] = useState(false)

  // Memoize initial state to prevent re-creating on every render
  const initialState = useMemo(
    () => ({
      searchTerm: '',
      selectedTab: 'firstbite' as const,
      filters: {
        category: '',
        type: '',
      },
      sortBy: 'relevance',
      firstBitePage: 1,
      unmatchedPage: 1,
      firstBiteDoorPage: 1,
      unmatchedDoorPage: 1,
    }),
    []
  )

  // Search state management
  const searchState = useUnifiedSearchState({
    initialState,
  })

  // Search options (categories, types, sort)
  const { categoryOptions, typeOptions, sortOptions, isOppLoading } =
    useUnifiedSearchOptions({
      filters: searchState.filters,
    })

  // API and search results
  const searchResults = useUnifiedSearchAPI({
    searchTerm: searchState.debouncedSearchTerm,
    filters: searchState.filters,
    sortBy: searchState.sortBy,
    firstBitePage: searchState.firstBitePage,
    unmatchedPage: searchState.unmatchedPage,
    firstBiteDoorPage: searchState.firstBiteDoorPage,
    unmatchedDoorPage: searchState.unmatchedDoorPage,
    selectedTab: searchState.selectedTab,
    pageSize: PAGE_SIZE,
  })

  // Use the originally clicked row for radio button selection
  const selectedSearchResult = selectedRow

  // Handle company/door row click
  const handleCompanyClick = useCallback(
    async (companyOrDoor: any) => {
      // Set selected row immediately for instant UI feedback
      setSelectedRow(companyOrDoor)

      if (searchState.selectedTab === 'firstbite') {
        // FirstBite company selection - need to resolve from item_id to contact_company_id
        const chainResult = companyOrDoor as any

        // The contact_company_id in the result is actually parsed from item_id (incorrectly)
        // We need to use the fake contact_company_id as the chain_id
        const chainId = chainResult.contact_company_id

        if (!chainId || isNaN(chainId)) {
          toast.error('Invalid company ID format', { autoClose: 5000 })
          setSelectedRow(null)
          return
        }

        setIsResolvingCompany(true)
        try {
          const resolved = await api.resolveCompanyFromFbIds({
            chainId,
            type: chainResult.type,
          })

          const companyData: ContactCompanySearchResult = {
            contact_company_id: resolved.contact_company_id,
            name: resolved.name,
            domain: chainResult.domain || '',
            city: resolved.city || chainResult.city || undefined,
            state: resolved.state || chainResult.state || undefined,
            address_line1: resolved.address_line1 ?? undefined,
            zipcode: resolved.zipcode ?? undefined,
          }

          setSelectedCompany(companyData)
          setSelectedDoor(null)
        } catch (error: any) {
          toast.error(
            error?.response?.data?.error ||
              'Unable to find this company in your contacts. Please add it first.',
            { autoClose: 5000 }
          )
          setSelectedRow(null)
          setSelectedCompany(null)
          setSelectedDoor(null)
          return
        } finally {
          setIsResolvingCompany(false)
        }
      } else if (searchState.selectedTab === 'unmatched') {
        // Unmatched company selection (already has contact_company_id)
        setSelectedCompany(companyOrDoor as ContactCompanySearchResult)
        setSelectedDoor(null)
      } else if (searchState.selectedTab === 'firstbitedoors') {
        // FirstBite door selection - need to resolve contact_company_id
        const door = companyOrDoor as DoorSearchResult

        if (!door.chain_id || !door.type) {
          toast.error(
            'This location cannot be selected because it is missing chain information.',
            { autoClose: 5000 }
          )
          setSelectedRow(null)
          return
        }

        setIsResolvingCompany(true)
        try {
          const resolved = await api.resolveCompanyFromFbIds({
            chainId: door.chain_id,
            doorId: door.id,
            type: door.type,
          })

          // Store door_proxy_id on the door object so it can be used later
          const doorWithProxyId = {
            ...door,
            door_proxy_id: resolved.door_proxy_id,
          }
          setSelectedDoor(doorWithProxyId)

          const companyData: ContactCompanySearchResult = {
            contact_company_id: resolved.contact_company_id,
            name: door.chain_name || resolved.name,
            domain: door.domain || '',
            city: resolved.city || door.city || undefined,
            state: resolved.state || door.state || undefined,
            address_line1: resolved.address_line1 || door.address || undefined,
            zipcode: resolved.zipcode || door.zipcode || undefined,
          }

          setSelectedCompany(companyData)
        } catch (error: any) {
          toast.error(
            error?.response?.data?.error ||
              'Unable to find this company in your contacts. Please add it first.',
            { autoClose: 5000 }
          )
          setSelectedRow(null)
          setSelectedCompany(null)
          setSelectedDoor(null)
          return
        } finally {
          setIsResolvingCompany(false)
        }
      } else if (searchState.selectedTab === 'unmatcheddoors') {
        // Unmatched door selection (already has chain_proxy_id)
        const door = companyOrDoor as DoorSearchResult

        // Use chain_proxy_id (ContactCompany ID) if available,
        // otherwise use chain_id (will be resolved on backend)
        if (!door.chain_proxy_id && !door.chain_id) {
          toast.error(
            'This location cannot be selected because it is not associated with a company. ' +
              'Please select a different location or use the company search tab.',
            { autoClose: 5000 }
          )
          setSelectedRow(null)
          return
        }

        setSelectedDoor(door)

        // Create a ContactCompanySearchResult from the door's chain proxy info
        // If chain_proxy_id exists, use it; otherwise pass chain_id to be resolved on backend
        const companyData = {
          contact_company_id: door.chain_proxy_id || undefined,
          chain_id: door.chain_id,
          name: door.chain_name || `Location ${door.id}`,
          domain: door.domain || '',
          city: door.city || undefined,
          state: door.state || undefined,
        } as ContactCompanySearchResult & { chain_id?: number }

        setSelectedCompany(companyData)
      }
    },
    [searchState.selectedTab, api]
  )

  // Handle form submission (enter key)
  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  // Handle select button click
  const handleSelectClick = useCallback(() => {
    if (selectedCompany) {
      onSelectCompany(selectedCompany, selectedDoor || undefined)
      setSelectedCompany(null)
      setSelectedDoor(null)
      setSelectedRow(null)
      onClose()
    }
  }, [selectedCompany, selectedDoor, onSelectCompany, onClose])

  // Handle modal close
  const handleClose = useCallback(() => {
    setSelectedCompany(null)
    setSelectedDoor(null)
    setSelectedRow(null)
    onClose()
  }, [onClose])

  // Reset search when modal closes
  useEffect(() => {
    if (!open) {
      searchState.setSearchTerm('')
      searchState.resetPagination()
    }
  }, [open, searchState])

  return (
    <Modal
      open={open}
      onOpenChange={(open) => !open && handleClose()}
      size="2xl"
      title="Search for Company"
      onAccept={handleSelectClick}
      acceptButtonText="Select Company"
      blockAccept={!selectedCompany || isResolvingCompany}
      loading={isResolvingCompany}
      onCancel={handleClose}
      cancelButtonText="Cancel"
      childrenContainerClassName="max-h-[80vh] overflow-y-auto overflow-x-auto max-w-full w-full"
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 p-2 space-y-4">
          {/* Search Input */}
          <UnifiedSearchInput
            searchTerm={searchState.searchTerm}
            setSearchTerm={searchState.setSearchTerm}
            onSubmit={handleSearchSubmit}
            searchType={searchResults.searchType}
            isLoading={searchResults.isLoading}
          />

          <hr className="mt-3 border-gray-400" />

          {/* Filters Row */}
          <UnifiedSearchFilters
            filters={searchState.filters}
            setFilters={searchState.setFilters}
            sortBy={searchState.sortBy}
            setSortBy={searchState.setSortBy}
            categoryOptions={categoryOptions}
            typeOptions={typeOptions}
            sortOptions={sortOptions}
            isOppLoading={isOppLoading}
          />

          {/* Show selected company/location or loading skeleton */}
          {(selectedRow || isResolvingCompany) && (
            <div className="bg-fb-green-50 border border-fb-green-200 rounded-lg p-3">
              {isResolvingCompany ? (
                // Loading skeleton with shimmer animation
                <div className="space-y-2">
                  <div className="h-4 bg-gradient-to-r from-fb-green-200 via-fb-green-100 to-fb-green-200 bg-[length:200%_100%] animate-shimmer rounded w-3/4"></div>
                  <div className="h-3 bg-gradient-to-r from-fb-green-200 via-fb-green-100 to-fb-green-200 bg-[length:200%_100%] animate-shimmer rounded w-1/2"></div>
                </div>
              ) : selectedCompany ? (
                // Actual content
                <>
                  <div className="text-sm font-medium text-fb-green-900">
                    Selected: {selectedCompany.name}
                    {selectedDoor && (
                      <span className="ml-2 text-xs font-normal text-fb-green-700">
                        (Company Level)
                      </span>
                    )}
                  </div>
                  {selectedDoor && (
                    <div className="text-sm font-medium text-fb-green-900 mt-1">
                      Location: {selectedDoor.name}
                    </div>
                  )}
                  {selectedDoor && selectedDoor.address && (
                    <div className="text-xs text-fb-green-700">
                      {selectedDoor.address}
                      {(selectedDoor.city || selectedDoor.state) && (
                        <span>
                          {', '}
                          {selectedDoor.city}
                          {selectedDoor.city && selectedDoor.state && ', '}
                          {selectedDoor.state}
                        </span>
                      )}
                    </div>
                  )}
                  {!selectedDoor &&
                    selectedCompany.city &&
                    selectedCompany.state && (
                      <div className="text-xs text-fb-green-700">
                        {selectedCompany.city}, {selectedCompany.state}
                      </div>
                    )}
                </>
              ) : null}
            </div>
          )}

          {/* Tabs and Results */}
          <ColumnsStoreProvider
            tableKey="company-search-modal"
            savedPresetsIdentifier={'COMPANY_SEARCH_MODAL' as any}
          >
            <DataTableProvider tableKey="company-search-modal">
              <UnifiedSearchResults
                selectedTab={searchState.selectedTab}
                setSelectedTab={searchState.setSelectedTab}
                firstBiteCount={searchResults.firstBiteCount}
                unmatchedCount={searchResults.unmatchedCount}
                firstBiteDoorCount={searchResults.firstBiteDoorCount}
                unmatchedDoorCount={searchResults.unmatchedDoorCount}
                currentResults={searchResults.currentResults}
                debouncedSearchTerm={searchState.debouncedSearchTerm}
                isLoading={searchResults.isLoading}
                currentTotalRows={searchResults.currentTotalRows}
                pageSize={PAGE_SIZE}
                currentPage={searchState.currentPage}
                setCurrentPage={searchState.setCurrentPage}
                onRowClick={handleCompanyClick}
                selectedItem={selectedSearchResult}
                showRadioButtons={true}
              />
            </DataTableProvider>
          </ColumnsStoreProvider>
        </div>
      </div>
    </Modal>
  )
}
